// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Single "Enter immersive mode" button — adapts to whatever the
 * device supports.
 *
 * Design choice: AR passthrough is strictly better than VR for the
 * Science On a Sphere use case (a virtual globe in your real room
 * IS the SOS experience), so when both are available we prefer AR.
 * VR is only used as a fallback for hardware that can't do
 * passthrough (PCVR via SteamVR, etc.).
 *
 *   AR + VR available → button reads "Enter AR", launches immersive-ar
 *   VR only           → button reads "Enter VR", launches immersive-vr
 *   Neither           → button hidden
 *
 * The button hides itself for the duration of an active session and
 * re-shows on session end via `onSessionEnd`.
 *
 * See {@link file://./../../docs/VR_INVESTIGATION_PLAN.md VR_INVESTIGATION_PLAN.md}
 * Phase 2.1 for the AR design discussion. If a future user really
 * wants the dark-void VR mode on AR-capable hardware, the right
 * place is a small "Mode" toggle in settings — deferred until
 * someone asks.
 */

import { isHandheldArUserAgent, isImmersiveVrSupported, isImmersiveArSupported, isWebXRAvailable } from '../utils/vrCapability'
import { arIntroSeen, openArIntroSheet } from './xrIntroSheet'
import { enterImmersive, isVrActive, loadThree, type VrMode, type VrSessionContext } from '../services/vrSession'
import { pauseForVrEntry, resumeForVrExit, TELEMETRY_BUILD_ENABLED } from '../analytics'
import { logger } from '../utils/logger'
import { t } from '../i18n'

const BUTTON_ID = 'vr-enter-btn'

/**
 * Surface a short user-visible message in the app's existing
 * `#error-message` banner (same DOM contract main.ts's `setError`
 * uses). Self-contained so this module doesn't need a handle on the
 * app instance; a missing banner (orbit.html, test fixtures) no-ops.
 */
function showVrErrorBanner(message: string): void {
  const errorEl = document.getElementById('error-message')
  if (!errorEl) return
  const textEl = document.getElementById('error-text')
  if (textEl) textEl.textContent = message
  errorEl.classList.remove('hidden')
  const dismissBtn = document.getElementById('error-dismiss') as HTMLButtonElement | null
  if (dismissBtn) {
    dismissBtn.onclick = () => {
      errorEl.classList.add('hidden')
    }
  }
}

/** How long the "not available here" notice stays up on its own. */
const VR_NOTICE_MS = 6000

/**
 * AYNI — a passing note, not an error: a small pill at the top of the
 * page that fades out by itself. For information the viewer can do
 * nothing about (this device has no AR/VR), where the red error box
 * read as if the whole site had failed.
 */
function showVrNotice(message: string, waitedMs = 0): void {
  // Support is known before the Earth has loaded; a note shown under the
  // loading screen would time out unseen, so it waits for it to go.
  const loading = document.getElementById('loading-screen')
  const loadingUp = !!loading && loading.style.display !== 'none' && !loading.classList.contains('fade-out')
  if (loadingUp && waitedMs < 60_000) {
    window.setTimeout(() => showVrNotice(message, waitedMs + 250), 250)
    return
  }
  document.querySelector('.vr-notice')?.remove()
  const el = document.createElement('div')
  el.className = 'vr-notice'
  el.setAttribute('role', 'status')
  const icon = document.createElement('span')
  icon.className = 'vr-notice-icon'
  icon.setAttribute('aria-hidden', 'true')
  icon.textContent = 'i'
  const text = document.createElement('span')
  text.textContent = message
  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'vr-notice-close'
  close.setAttribute('aria-label', t('vr.notice.dismiss'))
  close.textContent = '×'
  el.append(icon, text, close)
  // The first-visit privacy banner holds the same top-centre spot: sit under it.
  const banner = document.querySelector<HTMLElement>('.disclosure-banner')
  const bannerBottom = banner && !banner.hidden ? banner.getBoundingClientRect().bottom : 0
  if (bannerBottom > 0) el.style.top = `${Math.round(bannerBottom + 8)}px`
  document.body.appendChild(el)
  let timer = 0
  const dismiss = () => {
    window.clearTimeout(timer)
    el.classList.add('vr-notice-leaving')
    // Removed after the fade; at once when motion is reduced (no transition runs).
    el.addEventListener('transitionend', () => el.remove(), { once: true })
    window.setTimeout(() => el.remove(), 400)
  }
  close.addEventListener('click', dismiss)
  timer = window.setTimeout(dismiss, VR_NOTICE_MS)
}

/**
 * Wire the immersive-mode button. Safe to call even if the button
 * element is missing from the DOM — logs and no-ops. Safe to call
 * on every boot; idempotent for any given button element.
 */
export async function initVrButton(ctx: VrSessionContext): Promise<void> {
  const button = document.getElementById(BUTTON_ID) as HTMLButtonElement | null
  if (!button) {
    logger.debug('[VR] initVrButton: no #vr-enter-btn element, skipping')
    return
  }

  // Idempotent guard — if init has already run for this element,
  // skip. Without this, repeat calls (hot-reload, re-init after DOM
  // swap) stack click listeners and warm-load Three.js multiple
  // times. The dataset flag survives across calls as long as the
  // same DOM element sticks around.
  if (button.dataset.vrInitialized === 'true') return
  button.dataset.vrInitialized = 'true'

  // Detect both modes in parallel — no reason to serialize independent calls.
  const [vrSupported, arSupported] = await Promise.all([
    isImmersiveVrSupported(),
    isImmersiveArSupported(),
  ])

  if (!vrSupported && !arSupported) {
    logger.debug('[VR] Neither immersive mode supported — button stays hidden')
    // Tell the user WHY there's no enter button instead of hiding it
    // silently — but only when the browser actually speaks WebXR.
    // Browsers with no XR API at all (plain 2D desktops, Firefox,
    // Safari) keep the fully-silent treatment so the banner doesn't
    // become noise for users who were never going to see the button.
    if (isWebXRAvailable()) {
      showVrNotice(t('vr.error.unsupported'))
    }
    return
  }

  // Prefer AR — it's the better SOS experience. Fall back to VR for
  // hardware that lacks passthrough.
  const mode: VrMode = arSupported ? 'ar' : 'vr'
  const labelText = t(mode === 'ar' ? 'vr.enter.ar.label' : 'vr.enter.label')
  const titleText = t(mode === 'ar' ? 'vr.enter.ar.title' : 'vr.enter.title')

  // Update the visible label + accessibility attributes for whichever
  // mode the device picked. The DOM ships with "Enter VR" as a
  // sensible default for screen readers / no-JS browsers.
  const labelSpan = button.querySelector('.vr-btn-label')
  if (labelSpan) labelSpan.textContent = labelText
  // AYNI (the AR refresh): at phone width the long label hides; a short
  // one stays, so the button never shrinks to a bare icon.
  if (mode === 'ar' && !button.querySelector('.vr-btn-short')) {
    const short = document.createElement('span')
    short.className = 'vr-btn-short'
    short.setAttribute('aria-hidden', 'true')
    short.textContent = t('vr.enter.ar.short')
    button.appendChild(short)
  }
  button.title = titleText
  button.setAttribute('aria-label', titleText)
  button.classList.remove('hidden')
  button.setAttribute('aria-hidden', 'false')

  // Warm-load the Three.js chunk in the background now that we know
  // the device supports SOMETHING. The first click → session-start
  // becomes near-instant on good connections instead of waiting for
  // the ~180 KB gzipped download. Safe if the user never taps.
  void loadThree().catch(err =>
    logger.debug('[VR] Three.js prefetch failed (non-fatal):', err),
  )

  const sessionCtx: VrSessionContext = {
    ...ctx,
    onSessionEnd: (lastView) => {
      // Restore the button when the user comes back out, then
      // forward to the caller's own onSessionEnd (if any) so it can
      // do post-session UI work.
      button.classList.remove('hidden')
      button.setAttribute('aria-hidden', 'false')
      ctx.onSessionEnd?.(lastView)
    },
  }

  // AYNI (the AR refresh): a phone's first AR says what it offers and needs
  // before the camera opens (xrIntroSheet); its Start AR is the gesture the
  // session request needs. Headsets go straight in.
  const handheld = mode === 'ar' && isHandheldArUserAgent(navigator.userAgent)
  button.addEventListener('click', () => {
    if (button.classList.contains('pending')) return
    if (handheld && !arIntroSeen()) {
      openArIntroSheet({ datasetTitle: ctx.getDatasetTitle(), onStart: () => void start(), trigger: button })
      return
    }
    void start()
  })

  async function start(): Promise<void> {
    if (!button || button.classList.contains('pending')) return
    button.classList.add('pending')
    button.setAttribute('aria-busy', 'true')
    // Hand the GPU off to the immersive surface — pause the 2D
    // perf sampler so the two surfaces don't double-count median
    // FPS. The session's own teardown resumes it via the
    // onSessionEnd callback wired in main.ts.
    if (TELEMETRY_BUILD_ENABLED) pauseForVrEntry()
    try {
      await enterImmersive(mode, sessionCtx)
      // AYNI (Quest review C1): a session ended while it was starting (the
      // Meta button while the headset was black) has already been put
      // away, and onSessionEnd showed the button again.
      if (!isVrActive()) return
      // Session is live — hide the button so it doesn't overlap any
      // DOM that might be briefly visible during the transition.
      button.classList.add('hidden')
      button.setAttribute('aria-hidden', 'true')
      ctx.onSessionStart?.()
    } catch (err) {
      // Entry failed — resume sampling immediately so we don't
      // strand the sampler in the paused state.
      if (TELEMETRY_BUILD_ENABLED) resumeForVrExit()
      logger.error(`[VR] enterImmersive(${mode}) failed:`, err)
      // Surface the failure in the app's error banner (and keep the
      // title tooltip for hover users) — a silent return leaves the
      // user tapping a button that appears to do nothing.
      const message = err instanceof Error ? err.message : t('vr.error.failed', { mode: mode.toUpperCase() })
      button.title = message
      showVrErrorBanner(t('vr.error.failed', { mode: mode.toUpperCase() }))
    } finally {
      button.classList.remove('pending')
      button.removeAttribute('aria-busy')
    }
  }
}
